import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { Autocomplete, Checkbox, Grid, TextField, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';
import FacilityManagerChips from './FacilityManagerChips';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';
import Iconify from '../../../../reusable-components/iconify/Iconify';
import AwaitButton from '../../../../reusable-components/await-button/AwaitButton'
import { HOST_API_URL } from '../../../../config-global';
import PermissionHistoryIcon from '../users/PermissionHistoryIcon';

FacilityManagersTab.propTypes = {
  facilities: PropTypes.array,
};

export default function FacilityManagersTab({ facilities }) {
  const { enqueueSnackbar } = useSnackbar();
  const [tableData, setTableData] = useState([]);
  const [facilityManagers, setFacilityManagers] = useState([]);
  const [purchasingCategories, setPurchasingCategories] = useState([]);
  const [ticketCategories, setTicketCategories] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [updateExistingManagedItems, setUpdateExistingManagedItems] = useState(true);
  const [mode, setMode] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [useElectronicInvoiceManagers, setUseElectronicInvoiceManagers] = useState(false);

  useEffect(() => {
    axiosInstance
      .get('Admin/Facility/FacilityManagers')
      .then((res) => {
        setFacilityManagers(
          res.data.facilityManagers
            .map((manager) => ({ ...manager, lastSelected: 0 }))
            .sort((a, b) => (a.name ?? '').localeCompare(b.name))
        );
        setPurchasingCategories(res.data.purchasingCategories);
        setTicketCategories(res.data.ticketCategories);
        setUseElectronicInvoiceManagers(res.data.useElectronicInvoiceManagers);
        setTableData(
          tableDataJoin(
            res.data.facilityManagerValues,
            res.data.facilityManagers,
            facilities,
            res.data.purchasingCategories,
            res.data.ticketCategories,
            res.data.useElectronicInvoiceManagers
          )
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const tableDataJoin = (data, facilityManagers, facilities, purchasingCategories, ticketCategories, useElectronicInvoiceManagers) => {
    const facilityManagerValues = data.reduce((acc, curr) => {
      acc[`${curr.facilityId}-${curr.modelAttachmentId}-${curr.typeId}`] = facilityManagers.filter((manager) => curr.managerIds.includes(manager.id));
      return acc;
    }, []);

    return [
      ...facilities.flatMap((facility) =>
        purchasingCategories.map((category) => ({
          categoryId: category.value,
          category: category.label,
          facilityId: facility.id,
          facility: facility.facilityName,
          company: facility.company,
          type: 'Purchasing',
          typeId: 15,
          managers: facilityManagerValues[`${facility.id}-${category.value}-15`],
        }))
      ),
      ...facilities.flatMap((facility) =>
        ticketCategories.map((category) => ({
          categoryId: category.value,
          category: category.label,
          facilityId: facility.id,
          facility: facility.facilityName,
          company: facility.company,
          type: 'Tickets',
          typeId: 62,
          managers: facilityManagerValues[`${facility.id}-${category.value}-62`],
        }))
      ),
      ...facilities.map((facility) => ({
        categoryId: 0,
        category: '',
        facilityId: facility.id,
        facility: facility.facilityName,
        company: facility.company,
        type: 'Rentals',
        typeId: 71,
        managers: facilityManagerValues[`${facility.id}-0-71`],
      })),
      ...(!useElectronicInvoiceManagers ? [] : facilities.flatMap((facility) =>
        purchasingCategories.map((category) => ({
          categoryId: category.value,
          category: category.label,
          facilityId: facility.id,
          facility: facility.facilityName,
          company: facility.company,
          type: 'Invoices',
          typeId: 88,
          managers: facilityManagerValues[`${facility.id}-${category.value}-88`],
        }))
      ))
    ]
      .sort((a, b) => (a.facility ?? '').localeCompare(b.facility))
      .map((val, index) => ({ ...val, id: index }));
  };

  const fields = useMemo(
    () => [
      {
        field: 'facility',
        headerName: 'Facility',
        flex: 1,
        type: 'customSelect',
        minWidth: 150,
      },
      {
        field: 'company',
        headerName: 'Company',
        flex: 1,
        type: 'customSelect',
        minWidth: 150,
      },
      {
        field: 'category',
        headerName: 'Category',
        type: 'customSelect',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'customSelect',
        renderCell: (params) =><>
          {params.value}
          <PermissionHistoryIcon permissionTypeId={params.row.typeId} modelId={params.row.facilityId} modelAttachmentId={params.row.categoryId} />
        </>,
        minWidth: 150,
      },
      {
        field: 'managers',
        headerName: 'Managers',
        width: 300,
        type: 'customSelect',
        customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
        getFilterValues: (row) => row.managers?.map((manager) => manager.name),
        valueGetter: (params) => params.row.managers?.map((manager) => manager.name).join(', ') ?? '',
        renderCell: (params) => (
          <FacilityManagerChips
            row={params.row}
            facilityManagers={facilityManagers}
            setRowData={setTableData}
            getUpdateExistingManagedItems={() => {
              let curVal = false;
              setUpdateExistingManagedItems((val) => {
                curVal = val;
                return val;
              });
              return curVal;
            }}
          />
        ),
      },
    ], [facilityManagers]);

  const managerBulkSubmit = () => {
    setSubmitting(true);
    axiosInstance
      .post(
        'Admin/Facility/ManagerBulkSubmit',
        objectToFormData({
          permissions: tableData
            .filter((val) => selectedRows.includes(val.id))
            .map((row) => ({ typeId: row.typeId, facilityId: row.facilityId, categoryId: row.categoryId })),
          userIds: selectedManagers.map((man) => man.userId),
          mode,
          updateExistingManagedItems,
        })
      )
      .then((res) => {
        if (res.data !== null) {
          setTableData(tableDataJoin(res.data, facilityManagers, facilities, purchasingCategories, ticketCategories, useElectronicInvoiceManagers));
          enqueueSnackbar(`Managers Updated`, { variant: 'success' });
          setSelectedRows([]);
          setSelectedManagers([]);
          setMode(null);
        } else enqueueSnackbar(`Manager Update Failed`, { variant: 'error' });
      })
      .catch(() => enqueueSnackbar(`Manager Update Failed`, { variant: 'error' }))
      .finally(() => { setSubmitting(false); setShowConfirmDialog(false); });
  };

  const downloadFacilityManagers = () => window.open(`${HOST_API_URL}Api/Admin/Facility/DownloadFacilityManagers`, '_blank');

  return (
    <>
      {!loading ? (
        <CustomDataGrid
          gridId="admin-facility-managers-full"
          data={tableData}
          gridColumns={fields}
          keepNonExistentRowsSelected
          checkboxSelection={selectedManagers?.length > 0}
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(selectedItemIds) => {
            setSelectedRows(selectedItemIds);
          }}
          CustomLeftToolbar={() => (
            <Grid container spacing={2} alignItems="center" sx={{ mt: 0.3 }}>
              {mode && (
                <Grid item>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    limitTags={2}
                    size="small"
                    sx={{ '& span.MuiAutocomplete-tagSizeSmall ': { color: 'primary.main' }, width: 300 }}
                    value={selectedManagers}
                    onChange={(event, newValue) => {
                      setSelectedManagers(newValue);
                      setSelectedRows([]);
                    }}
                    isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                    options={
                      facilityManagers?.map((manager) => ({
                        value: manager.id,
                        label: manager.name,
                        userId: manager.userId,
                      })) ?? []
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={mode === 'add' ? 'Choose Manager to Add' : 'Choose Manager to Remove'}
                        size="small"
                        autoComplete="off"
                      />
                    )}
                  />
                </Grid>
              )}
              {!mode && (
                <Grid item>
                  <Button variant="contained" sx={{ mr: 1 }} size="small" onClick={() => setMode('add')}>
                    Add Manager
                  </Button>
                  <Button variant="contained" sx={{ mr: 1 }} size="small" onClick={() => setMode('remove')}>
                    Remove Manager
                  </Button>
                  <AwaitButton
                    variant="outlined"
                    sx={{ mr: 1 }}
                    size="small"
                    color="secondary"
                    onClick={downloadFacilityManagers}
                    startIcon={<Iconify icon="file-icons:microsoft-excel" />}
                  >
                    Download
                  </AwaitButton>
                </Grid>
              )}
              {selectedRows.length > 0 && (
                <Grid item>
                  <Button loading={submitting} variant="contained" size="small" onClick={() => setShowConfirmDialog(true)}>
                    {mode === 'add' ? ' Add' : ' Remove'}
                  </Button>
                </Grid>
              )}
              {mode && (
                <Grid item>
                  <Button
                    variant="underlined"
                    color="secondary"
                    size="small"
                    onClick={() => {
                      setMode(null);
                      setSelectedManagers([]);
                      setSelectedRows([]);
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              )}
              {mode && (
                <Grid item>
                  <Checkbox
                    disableRipple
                    checked={updateExistingManagedItems}
                    onChange={() => setUpdateExistingManagedItems((val) => !val)}
                  />
                  Update Existing Managed Items
                </Grid>
              )}
            </Grid>
          )}
        />
      ) : (
        <Typography sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 5 }}>
          <LoadingAnimation />
        </Typography>
      )}

      <Dialog maxWidth="xs" fullWidth open={showConfirmDialog} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        setShowConfirmDialog(false);
      }}>
        <DialogTitle sx={{ pb: 2 }} textAlign="center">{mode === 'add' ? ' Add' : ' Remove'} Managers</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "large", fontWeight: "bold" }}>Managers</Typography>
          <Typography sx={{ fontSize: "small", fontWeight: "bold" }} pb={2}>{selectedManagers.map(manager => manager.label).join(", ")}</Typography>
          <Typography sx={{ fontSize: "large", fontWeight: "bold" }}>Facilities/Permissions</Typography>
          {Object.entries(tableData
            .filter((val) => selectedRows.includes(val.id)).reduce((acc, nextVal) => { acc[nextVal.facility] = [...(acc[nextVal.facility] ?? []), nextVal]; return acc; }, {}))
            .map(([key, val]) => <Typography sx={{ fontSize: ".9rem", fontWeight: "bold" }} key={key}>
              {key}
              {Object.entries(val.reduce((acc, nextVal) => { acc[nextVal.type] = (acc[nextVal.type] ?? 0) + 1; return acc; }, {})).map(([key, val]) => <Typography sx={{ fontSize: "small", fontWeight: "bold" }} key={key}>
                {key} - {val}
              </Typography>)}
            </Typography>)}
        </DialogContent>

        <DialogActions>
          <LoadingButton loading={submitting} variant="contained" onClick={managerBulkSubmit} color={mode === 'add' ? 'primary' : 'error'}>
            {mode === 'add' ? ' Add' : ' Remove'}
          </LoadingButton>

          <Button variant="outlined" color="inherit" onClick={() => {
            setShowConfirmDialog(false);
            setMode(null);
            setSelectedManagers([]);
            setSelectedRows([]);
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
